import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map } from 'immutable';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';

import Grid from '../../helpers/AgGridTable';
import config from '../../../config';
import { DATE_FORMAT } from '../../../utils/dateFormat';
import { RowIndex } from '../RowIndex';
import { CompanyCell } from '../CompanyCell';
import { PriorityRadio } from '../PriorityRadio';
import { Sales } from '../Sales';
import Checkbox from '../../helpers/Checkbox';
import { Products } from '../Products';
import { Markets } from '../Markets';
import { Facilities } from '../Facilities';
import { CustomFields } from '../CustomFields';
import { Description } from '../Description';
import MultilineTextEditor from '../MultilineTextEditor';
import { BuyerComment } from '../BuyerComment';
import { Card } from '../../helpers/Card';
import { GridCard } from '../../helpers/GridCard';
import Button from '../../helpers/Button';

import style from './style.module.css';

const getRowNodeId = data => data.targetBuyerId;

const OnlineBodySection = ({ onlineApproval, onSaveProgress, onSubmit, onUpdate, saving }) => {
  const [agree, setAgree] = useState(false);
  const [requiredRows, setRequiredRows] = useState([]);
  const [firstName, setFirstName] = useState(onlineApproval.get('completedByFirstName'));
  const [lastName, setLastName] = useState(onlineApproval.get('completedByLastName'));

  const apiRef = useRef(null);
  const containerRef = useRef(null);
  const onlineApprovalRef = useRef(onlineApproval);

  onlineApprovalRef.current = onlineApproval;

  const getRequiredRowsList = useCallback(() => {
    const approvals = onlineApprovalRef.current.get('approvalListData').toJS();
    const nextRequiredRows = [];
    const approvalsList = approvals.map(approval => approval.targetBuyerPriority);
    const emptyPriority = '';
    let indexes = approvalsList.indexOf(emptyPriority);

    while (indexes !== -1) {
      nextRequiredRows.push(indexes + 1);
      indexes = approvalsList.indexOf(emptyPriority, indexes + 1);
    }

    setRequiredRows(nextRequiredRows);
  }, []);

  const updateTable = useCallback(
    data => {
      const approvals = onlineApprovalRef.current
        .get('approvalListData')
        .toJS()
        .map(priority => {
          if (priority.targetBuyerId === data.targetBuyerId) {
            return data;
          }

          return priority;
        });

      onUpdate(approvals);
    },
    [onUpdate],
  );

  const handlePriorityChange = useCallback(
    async (event, data) => {
      await updateTable(data);

      const rowNode = apiRef.current.getRowNode(data.targetBuyerId);

      apiRef.current.refreshCells({
        columns: ['bsnClient', 'priority'],
        rowNodes: [rowNode],
        force: true,
      });

      getRequiredRowsList();
    },
    [getRequiredRowsList, updateTable],
  );

  const columns = useMemo(
    () =>
      config.tables.getIn(['approval', 'online']).map(column => {
        const searchParams = new URLSearchParams(window.location.search);
        const optionalFields = [];
        const isCompletedList = Boolean(onlineApproval.get('dateReceived'));

        // eslint-disable-next-line no-restricted-syntax
        for (const value of searchParams.keys()) {
          optionalFields.push(value);
        }

        if (optionalFields.includes(column.get('field'))) {
          // eslint-disable-next-line no-param-reassign
          column = column.set('hide', false);
        }

        switch (column.get('field')) {
          case 'index':
            return column.merge({
              cellRendererFramework: RowIndex,
            });

          case 'companyLegalName':
            return column.merge({
              cellRendererFramework: CompanyCell,
            });

          case 'companyDescription':
            return column.merge({
              cellRendererFramework: Description,
            });

          case 'products':
            return column.merge({
              cellRendererFramework: Products,
            });

          case 'markets':
            return column.merge({
              cellRendererFramework: Markets,
            });

          case 'facilities':
            return column.merge({
              cellRendererFramework: Facilities,
            });

          case 'custom_fields':
            return column.merge({
              cellRendererFramework: CustomFields,
            });

          case 'companyRevenue':
            return column.merge({
              cellRendererFramework: Sales,
            });

          case 'priority':
            return column.merge({
              suppressFlash: true,
              cellRendererFramework: PriorityRadio,
              cellEditorFramework: PriorityRadio,
              cellRendererParams: {
                onChange: (event, data) => handlePriorityChange(event, data),
                isDisabled: isCompletedList,
              },
            });

          case 'bsnClient':
            return column.merge({
              hide: false,
              editable: true,
              cellRendererFramework: BuyerComment,
              cellEditorFramework: MultilineTextEditor,
            });

          default:
            return column;
        }
      }),
    // Built once on mount (like the old class field) so ag-grid doesn't churn cell renderers on every update.
    [],
  );

  const handleFirstDataRendered = useCallback(() => {
    getRequiredRowsList();
  }, [getRequiredRowsList]);

  const handleGridReady = useCallback(params => {
    apiRef.current = params.api;
    apiRef.current.sizeColumnsToFit();
  }, []);

  const handleGridSizeChanged = useCallback(() => {
    if (apiRef.current) {
      apiRef.current.sizeColumnsToFit();
    }
  }, []);

  const handleAgreeChange = useCallback(event => {
    setAgree(event.target.checked);
  }, []);

  const handleFirstNameChange = useCallback(event => {
    setFirstName(event.target.value.trim());
  }, []);

  const handleLastNameChange = useCallback(event => {
    setLastName(event.target.value.trim());
  }, []);

  /** Prepare params for save online approvals and save progress online approvals requests. */
  const prepareSaveOnlineApprovalsParams = useCallback(() => {
    const onlineList = onlineApproval
      .get('approvalListData')
      .map(approval => ({
        id: approval.get('id'),
        targetBuyerId: approval.get('targetBuyerId'),
        bsnClient: approval.get('bsnClient'),
        targetBuyerPriority: approval.get('targetBuyerPriority'),
      }))
      .toJS();

    return { onlineList };
  }, [onlineApproval]);

  const handleSubmit = useCallback(() => {
    apiRef.current.stopEditing();

    const { onlineList } = prepareSaveOnlineApprovalsParams();

    onSubmit(onlineList, { firstName, lastName });
  }, [firstName, lastName, onSubmit, prepareSaveOnlineApprovalsParams]);

  const handleSaveProgress = useCallback(() => {
    apiRef.current.stopEditing();

    const { onlineList } = prepareSaveOnlineApprovalsParams();

    onSaveProgress(onlineList, { firstName, lastName });
  }, [firstName, lastName, onSaveProgress, prepareSaveOnlineApprovalsParams]);

  const handleCellClicked = useCallback(cell => {
    const {
      column: { colId },
      node: { rowIndex },
    } = cell;

    apiRef.current.startEditingCell({ rowIndex, colKey: colId });
  }, []);

  const handleCellEditingStopped = useCallback(
    ({ data }) => {
      updateTable(data);
      apiRef.current.refreshCells({
        columns: ['bsnClient', 'priority'],
        force: true,
        suppressFlash: true,
      });
    },
    [updateTable],
  );

  const handleClickOutside = useCallback(event => {
    if (containerRef.current && containerRef.current.contains(event.target)) {
      return;
    }

    if (apiRef.current) {
      apiRef.current.stopEditing();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside]);

  const clientName = onlineApproval.get('buyerLegalName');
  const requiredRowsMessage = requiredRows.toString();
  const dateReceived = onlineApproval.get('dateReceived');
  const completedByFirstName = onlineApproval.get('completedByFirstName');
  const completedByLastName = onlineApproval.get('completedByLastName');
  const isCompletedList = Boolean(dateReceived);
  const isFulfilledName = firstName && lastName;

  return (
    <>
      <GridCard className={style.grid}>
        <div ref={containerRef} className={style.gridInner}>
          <Grid
            columnDefs={columns}
            getRowNodeId={getRowNodeId}
            headerHeight={35}
            onCellClicked={handleCellClicked}
            onCellEditingStopped={handleCellEditingStopped}
            onFirstDataRendered={handleFirstDataRendered}
            onGridReady={handleGridReady}
            onGridSizeChanged={handleGridSizeChanged}
            rowData={onlineApproval.get('approvalListData')}
            rowHeight={130}
          />
        </div>
      </GridCard>
      <Card className={style.host}>
        {requiredRows.length > 0 && (
          <p className={style.error}>Need to select a priority for {requiredRowsMessage} row(s)</p>
        )}
        {!agree && !isCompletedList && (
          <p className={style.error}>Need to click Approval List acknowledgment in lower left corner</p>
        )}
        {!isFulfilledName && !isCompletedList && (
          <p className={style.error}>Need to input First and Last name of Approval List submitter at bottom of page</p>
        )}
        {isCompletedList ? (
          <p className={style.completed}>
            This Approval List has been completed and submitted by {completedByFirstName} {completedByLastName} on{' '}
            {dayjs(dateReceived).format(DATE_FORMAT)}
          </p>
        ) : (
          <div className={style.submit}>
            <div className={style.agreement}>
              <Checkbox
                capitalizeLabel={false}
                id="agree"
                label={`Pursuant to the engagement agreement, the prioritized companies (except for Excluded targets)
                  on the preceding Approval List shall be deemed Approved Target Companies to be approached
                  by Harvey & Company LLC on behalf of ${clientName}. This list constitutes Harvey Confidential Information under the agreement.`}
                name="agree"
                onChange={handleAgreeChange}
              />
            </div>
            <div className="form-group">
              <label className="field-label" htmlFor="onlineApprovalFirstName">
                First Name
              </label>
              <input
                className="form-control"
                id="onlineApprovalFirstName"
                onChange={handleFirstNameChange}
                value={firstName}
              />
            </div>
            <div className="form-group">
              <label className="field-label" htmlFor="onlineApprovalLastName">
                Last Name
              </label>
              <input
                className="form-control"
                id="onlineApprovalLastName"
                onChange={handleLastNameChange}
                value={lastName}
              />
            </div>
            <div className={style.buttons}>
              <Button className="btn-primary" disabled={saving} onClick={handleSaveProgress}>
                Save Progress
              </Button>
              <Button
                className="btn-primary"
                disabled={!agree || requiredRows.length > 0 || saving || firstName === '' || lastName === ''}
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </div>
          </div>
        )}
      </Card>
    </>
  );
};

OnlineBodySection.propTypes = {
  onlineApproval: PropTypes.instanceOf(Map).isRequired,
  onSaveProgress: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  saving: PropTypes.bool.isRequired,
};

export default memo(OnlineBodySection);
